import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const font = "system-ui, -apple-system, 'Segoe UI', sans-serif";

const PLATFORM_COLORS = {
  meta:    { bg: 'rgba(24,119,242,0.10)', color: '#1877f2', label: 'Meta' },
  google:  { bg: 'rgba(66,133,244,0.10)', color: '#4285f4', label: 'Google' },
  tiktok:  { bg: 'rgba(0,0,0,0.06)',      color: '#333',    label: 'TikTok' },
  spotify: { bg: 'rgba(30,215,96,0.10)',  color: '#1ed760', label: 'Spotify' },
  linkedin:{ bg: 'rgba(0,119,181,0.10)',  color: '#0077b5', label: 'LinkedIn' },
};

function StatusBadge({ daysSince }) {
  if (daysSince === null || daysSince === undefined) {
    return <span style={{ fontSize: 11, fontWeight: 700, color: '#888', background: '#f0f0ee', borderRadius: 6, padding: '3px 10px' }}>Nessun dato</span>;
  }
  if (daysSince === 0) {
    return <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#dcfce7', borderRadius: 6, padding: '3px 10px' }}>✓ Oggi</span>;
  }
  if (daysSince === 1) {
    return <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#dcfce7', borderRadius: 6, padding: '3px 10px' }}>✓ Ieri</span>;
  }
  if (daysSince <= 3) {
    return <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fef3c7', borderRadius: 6, padding: '3px 10px' }}>⚠ {daysSince}gg fa</span>;
  }
  return <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', background: '#fee2e2', borderRadius: 6, padding: '3px 10px' }}>✗ {daysSince}gg fa</span>;
}

function fmtEur(n) {
  if (!n) return '—';
  return '€' + Number(n).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function DataHealth() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase.rpc('get_data_health');
      if (err) throw err;
      setData(rows || []);
      setLastRefresh(new Date());
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Raggruppa per cliente
  const byClient = {};
  data.forEach(row => {
    if (!byClient[row.client_name]) byClient[row.client_name] = { client_id: row.client_id, platforms: [] };
    byClient[row.client_name].platforms.push(row);
  });

  // Stats globali
  const totalOk    = data.filter(r => r.days_since_sync <= 1).length;
  const totalWarn  = data.filter(r => r.days_since_sync > 1 && r.days_since_sync <= 3).length;
  const totalError = data.filter(r => r.days_since_sync > 3 || r.days_since_sync === null).length;

  return (
    <div style={{ padding: '32px', fontFamily: font, color: '#1a1a1a', minHeight: '100vh', background: '#f5f5f3' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 21, fontWeight: 700, margin: 0, marginBottom: 4 }}>Data Health</h1>
          <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
            Stato sincronizzazione dati per cliente e piattaforma
            {lastRefresh && <span style={{ marginLeft: 8 }}>— aggiornato alle {lastRefresh.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ padding: '8px 16px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500, color: '#374151', fontFamily: font, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {loading ? '...' : '↻ Aggiorna'}
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
        {[
          { label: 'Sync aggiornate', value: totalOk,    color: '#16a34a', bg: '#dcfce7' },
          { label: 'In ritardo (1-3gg)', value: totalWarn,  color: '#d97706', bg: '#fef3c7' },
          { label: 'Non aggiornate',   value: totalError, color: '#dc2626', bg: '#fee2e2' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: s.color }}>
              {s.value}
            </div>
            <div style={{ fontSize: 13, color: '#555' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
          Errore: {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 48, color: '#888', fontSize: 13 }}>Caricamento...</div>
      )}

      {/* Per cliente */}
      {!loading && Object.entries(byClient).map(([clientName, { platforms }]) => {
        const worstSync = Math.max(...platforms.map(p => p.days_since_sync ?? 999));
        return (
          <div key={clientName} style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
            {/* Client header */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0ee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: worstSync <= 1 ? '#f0fdf4' : worstSync <= 3 ? '#fffbeb' : '#fef2f2' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>{clientName}</span>
              <span style={{ fontSize: 12, color: '#888' }}>{platforms.length} piattaform{platforms.length === 1 ? 'a' : 'e'}</span>
            </div>

            {/* Platform rows */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  {['Piattaforma', 'Stato', 'Ultima sync', 'Prima sync', 'Giorni dati', 'Campagne', 'Spend totale'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, textAlign: h === 'Piattaforma' ? 'left' : 'right', borderBottom: '1px solid #f0f0ee', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {platforms.map((row, i) => {
                  const p = PLATFORM_COLORS[row.platform] || PLATFORM_COLORS.meta;
                  return (
                    <tr key={row.platform} style={{ borderBottom: i < platforms.length - 1 ? '1px solid #f9f9f9' : 'none' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: p.color, background: p.bg, borderRadius: 5, padding: '3px 10px' }}>
                          {p.label || row.platform}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <StatusBadge daysSince={row.days_since_sync} />
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, color: '#374151', fontWeight: 500 }}>
                        {fmtDate(row.last_sync_date)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 12, color: '#9ca3af' }}>
                        {fmtDate(row.first_sync_date)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, color: '#374151' }}>
                        {Number(row.days_with_data).toLocaleString('it-IT')}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, color: '#374151' }}>
                        {Number(row.campaigns_total).toLocaleString('it-IT')}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#1a1a1a', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtEur(row.total_spend)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
